/**
 * Reportes: IVA y rentabilidad por consultorio.
 *
 * Todo se calcula en el servidor. Acá no se suma nada: el dinero no se opera en
 * el navegador.
 */

import { useState } from 'react';
import { addMonths, startOfMonth, today } from '@cleanblue/shared';
import { useApi } from '@/lib/hooks';
import { Async, Card, Cell, Empty, Money, Note, PageHead, Stat, Table } from '@/components/ui';

interface TaxReport {
  from: string; to: string; taxable: string; tax: string; invoices: number;
}

interface UnitReport {
  from: string;
  units: {
    id: string; unit_code: string; name: string | null;
    income_minor: number; cost_minor: number; supplies_minor: number; visits: number;
  }[];
}

export function ReportsPage() {
  const [from, setFrom] = useState(() => startOfMonth(today()));
  const [to, setTo] = useState(() => today());

  const tax = useApi<TaxReport>(`/admin/reports/tax?from=${from}&to=${to}`);
  const byUnit = useApi<UnitReport>(`/admin/reports/by-unit?from=${addMonths(today(), -6)}`);

  return (
    <>
      <PageHead
        title="Reportes"
        subtitle="Para el contador y para saber qué consultorio deja margen."
        actions={
          <>
            <input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 150 }} />
            <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: 150 }} />
            <a className="btn" href="/api/admin/export/receivables">Exportar cartera</a>
          </>
        }
      />

      <Card title="Impuesto sobre el valor agregado">
        <Note tone="info">
          Sale de las facturas <strong>efectivamente emitidas</strong> en el rango, no de los cobros
          generados. Es lo que el contador necesita declarar.
        </Note>
        <Async query={tax}>
          {(data) => (
            <div className="grid grid--3 mt-2">
              <Stat label="Base gravada" value={<Money value={data.taxable} size="lg" />} />
              <Stat accent label="IVA" value={<Money value={data.tax} size="lg" />} />
              <Stat label="Facturas emitidas" value={data.invoices} />
            </div>
          )}
        </Async>
      </Card>

      <div className="mt-3">
        <Async query={byUnit} empty={(d) => d.units.length === 0 && (
          <Empty icon="consultorio" title="Sin datos todavía" />
        )}>
          {(data) => (
            <Card title="Rentabilidad por consultorio · últimos 6 meses" flush>
              <Table head={
                <tr>
                  <th>Consultorio</th><th className="right">Ingresos</th>
                  <th className="right">Insumos</th><th className="right">Otros costos</th>
                  <th className="right">Margen</th><th className="right">Visitas</th>
                </tr>
              }>
                {data.units.map((u) => {
                  const margin = u.income_minor - u.cost_minor - u.supplies_minor;
                  return (
                    <tr key={u.id}>
                      <td className="strong"><Cell sub={u.name}>{u.unit_code}</Cell></td>
                      <td className="right"><Money value={u.income_minor} /></td>
                      <td className="right"><Money value={u.supplies_minor} dim={u.supplies_minor === 0} /></td>
                      <td className="right"><Money value={u.cost_minor} dim={u.cost_minor === 0} /></td>
                      <td className="right strong"><Money value={margin} /></td>
                      <td className="right muted">{u.visits}</td>
                    </tr>
                  );
                })}
              </Table>
            </Card>
          )}
        </Async>
      </div>
    </>
  );
}
